import React, { useState, useEffect, useRef } from 'react';
import { API } from '../contexts/AuthContext';
import axios from 'axios';
import Swal from 'sweetalert2';
import Tooltip from './Tooltip';

// Visual da plaquinha POR CAMPANHA (mkt_campanhas.plaq_*): cor da grade, imagem de topo
// (faixa da promoção, escolhida numa galeria compartilhada) e risco no preço DE.
// Salva sozinho a cada mudança — o PDF gerado usa sempre o que está gravado na campanha.
// A prévia vem do backend (mesmo gerador do PDF), então o que aparece é o que imprime.

const CORES = [
  { valor: null, nome: 'Automática', amostra: 'linear-gradient(135deg, #1A6B2A 50%, #2E3399 50%)', dica: 'Verde para De Linha, azul para Fora de Linha (como sempre foi)' },
  { valor: '#111111', nome: 'Preto' },
  { valor: '#1A6B2A', nome: 'Verde' },
  { valor: '#2E3399', nome: 'Azul' },
  { valor: '#6B2C5C', nome: 'Roxo' },
  { valor: '#C00000', nome: 'Vermelho' },
];

const toast = (title, icon = 'success') => Swal.fire({ toast: true, position: 'top-end', icon, title, showConfirmButton: false, timer: 1800 });

const lerArquivoBase64 = (file) => new Promise((resolve, reject) => {
  const r = new FileReader();
  r.onload = () => resolve(r.result);
  r.onerror = reject;
  r.readAsDataURL(file);
});

export default function PlaquinhaConfig({ campanhaId }) {
  const [config, setConfig] = useState(null); // { cor, topo, riscar_de }
  const [topos, setTopos] = useState([]);
  const [previewHtml, setPreviewHtml] = useState('');
  const [subindo, setSubindo] = useState(false);
  const [aberto, setAberto] = useState(true);
  const salvarTimer = useRef(null);
  const inputArquivo = useRef(null);

  const carregarTopos = () => axios.get(`${API}/plaquinhas/topos`).then(r => setTopos(r.data)).catch(() => {});

  useEffect(() => {
    setConfig(null);
    if (!campanhaId) return;
    axios.get(`${API}/plaquinhas/campanha/${campanhaId}/config`).then(r => setConfig(r.data)).catch(() => {});
    carregarTopos();
  }, [campanhaId]);

  // Prévia (debounce leve — a cor personalizada dispara onChange enquanto arrasta)
  useEffect(() => {
    if (!config) return;
    const t = setTimeout(() => {
      axios.post(`${API}/plaquinhas/preview`, config).then(r => setPreviewHtml(r.data.html)).catch(() => {});
    }, 250);
    return () => clearTimeout(t);
  }, [config]);

  useEffect(() => () => clearTimeout(salvarTimer.current), []);

  const alterar = (campo, valor, espera = 0) => {
    const novo = { ...config, [campo]: valor };
    setConfig(novo);
    clearTimeout(salvarTimer.current);
    salvarTimer.current = setTimeout(async () => {
      try {
        await axios.put(`${API}/plaquinhas/campanha/${campanhaId}/config`, novo);
        toast('Visual da plaquinha salvo');
        if (campo === 'topo') carregarTopos(); // atualiza o "em uso"
      } catch (err) {
        Swal.fire('Erro', err.response?.data?.error || 'Erro ao salvar o visual da plaquinha', 'error');
      }
    }, espera);
  };

  const subirTopo = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!/^image\/(png|jpeg)$/.test(file.type)) return Swal.fire('Atenção', 'Envie uma imagem PNG ou JPG.', 'warning');
    if (file.size > 10 * 1024 * 1024) return Swal.fire('Atenção', 'Imagem muito grande (máx. 10 MB).', 'warning');
    setSubindo(true);
    try {
      const imagem_base64 = await lerArquivoBase64(file);
      const r = await axios.post(`${API}/plaquinhas/topos`, { imagem_base64 });
      await carregarTopos();
      alterar('topo', r.data.arquivo); // já escolhe a que acabou de subir
    } catch (err) {
      Swal.fire('Erro', err.response?.data?.error || 'Erro ao subir a imagem', 'error');
    } finally { setSubindo(false); }
  };

  const excluirTopo = async (t) => {
    const emUsoAqui = config.topo === t.arquivo;
    const ok = await Swal.fire({
      title: 'Excluir imagem de topo?',
      text: emUsoAqui
        ? 'Ela está em uso nesta campanha: as plaquinhas ficam sem topo e a imagem sai da galeria.'
        : 'Ela sai da galeria e não poderá ser escolhida em outras campanhas.',
      icon: 'warning', showCancelButton: true, confirmButtonText: 'Excluir', cancelButtonText: 'Cancelar', confirmButtonColor: '#dc3545'
    });
    if (!ok.isConfirmed) return;
    try {
      await axios.delete(`${API}/plaquinhas/topos/${t.arquivo}`, { params: { campanha_id: campanhaId } });
      if (emUsoAqui) setConfig(c => ({ ...c, topo: null }));
      carregarTopos();
    } catch (err) {
      Swal.fire('Não foi possível excluir', err.response?.data?.error || 'Erro ao excluir a imagem', 'error');
    }
  };

  if (!campanhaId || !config) return null;

  const corPersonalizada = config.cor && !CORES.some(c => c.valor === config.cor);
  const resumo = [
    config.cor ? (CORES.find(c => c.valor === config.cor)?.nome || config.cor) : 'cor automática',
    config.topo ? 'com topo' : 'sem topo',
    config.riscar_de ? 'DE riscado' : null,
  ].filter(Boolean).join(' · ');

  return (
    <div className="card p-3 mb-3">
      <div className="d-flex justify-content-between align-items-center" style={{ cursor: 'pointer' }} onClick={() => setAberto(a => !a)}>
        <h6 className="fw-bold mb-0">
          <i className="bi bi-palette me-2"></i>Visual da plaquinha desta campanha
          <small className="text-muted fw-normal ms-2">({resumo})</small>
        </h6>
        <i className={`bi bi-chevron-${aberto ? 'up' : 'down'}`}></i>
      </div>

      {aberto && (
        <div className="row g-3 mt-1">
          <div className="col-lg-6">
            {/* 1. Cor da grade */}
            <label className="form-label fw-semibold mb-1" style={{ fontSize: '0.85rem' }}>Cor da grade</label>
            <div className="d-flex flex-wrap gap-2 mb-3">
              {CORES.map(c => {
                const ativo = config.cor === c.valor;
                return (
                  <Tooltip key={c.nome} texto={c.dica}>
                    <button type="button" onClick={() => alterar('cor', c.valor)}
                      className={`btn btn-sm ${ativo ? 'btn-primary-mkt' : 'btn-outline-secondary'} d-flex align-items-center gap-1`}>
                      <span style={{ width: 14, height: 14, borderRadius: 3, background: c.amostra || c.valor, border: '1px solid rgba(0,0,0,.2)' }} />
                      {c.nome}
                    </button>
                  </Tooltip>
                );
              })}
              <label className={`btn btn-sm ${corPersonalizada ? 'btn-primary-mkt' : 'btn-outline-secondary'} d-flex align-items-center gap-1 mb-0`}>
                <input type="color" value={corPersonalizada ? config.cor : '#000000'}
                  onChange={e => alterar('cor', e.target.value, 600)}
                  style={{ width: 18, height: 16, padding: 0, border: 'none', background: 'none', cursor: 'pointer' }} />
                Outra
              </label>
            </div>

            {/* 2. Imagem de topo */}
            <label className="form-label fw-semibold mb-1" style={{ fontSize: '0.85rem' }}>
              Imagem de topo (faixa da promoção)
              <Tooltip texto="Faixa no alto da plaquinha, como no modelo Black Friday. Tamanho ideal: 2400 × 300 px (proporção 8:1), PNG ou JPG. A imagem fica na galeria e pode ser usada em outras campanhas.">
                <i className="bi bi-info-circle ms-1 text-muted"></i>
              </Tooltip>
            </label>
            <div className="d-flex flex-column gap-2 mb-3">
              <button type="button" onClick={() => alterar('topo', null)}
                className={`btn btn-sm text-start ${!config.topo ? 'btn-primary-mkt' : 'btn-outline-secondary'}`}>
                <i className="bi bi-slash-circle me-1"></i>Sem topo
              </button>
              {topos.map(t => {
                const ativo = config.topo === t.arquivo;
                const outras = t.campanhas.filter(c => String(c.id) !== String(campanhaId));
                return (
                  <div key={t.arquivo} className="d-flex align-items-center gap-2 p-1"
                    style={{ border: `2px solid ${ativo ? '#6B2C5C' : '#e5e5e5'}`, borderRadius: 6, background: ativo ? '#F5EBF2' : '#fff' }}>
                    <img src={`${API}${t.url}`} alt="" onClick={() => alterar('topo', t.arquivo)}
                      style={{ width: 272, maxWidth: '70%', aspectRatio: '8 / 1', objectFit: 'cover', borderRadius: 3, cursor: 'pointer' }} />
                    <span className="flex-grow-1" style={{ fontSize: '0.78rem', cursor: 'pointer' }} onClick={() => alterar('topo', t.arquivo)}>
                      {ativo ? <b style={{ color: '#6B2C5C' }}><i className="bi bi-check-circle-fill me-1"></i>Em uso nesta campanha</b> : <span className="text-muted">Clique para usar</span>}
                    </span>
                    {outras.length > 0 && (
                      <Tooltip texto={`Também usada em: ${outras.map(c => c.nome).join(', ')}`}>
                        <span className="badge bg-light text-muted border">{outras.length} camp.</span>
                      </Tooltip>
                    )}
                    <button type="button" className="btn btn-sm btn-outline-danger" title="Excluir da galeria" onClick={() => excluirTopo(t)}>
                      <i className="bi bi-trash"></i>
                    </button>
                  </div>
                );
              })}
              <input ref={inputArquivo} type="file" accept="image/png,image/jpeg" className="d-none" onChange={subirTopo} />
              <button type="button" className="btn btn-sm btn-outline-mkt" disabled={subindo} onClick={() => inputArquivo.current?.click()}>
                {subindo ? <><span className="spinner-border spinner-border-sm me-1" />Subindo...</> : <><i className="bi bi-upload me-1"></i>Subir nova imagem de topo</>}
              </button>
            </div>

            {/* 3. Risco no DE */}
            <div className="form-check form-switch">
              <input className="form-check-input" type="checkbox" id="plaq-riscar-de" checked={!!config.riscar_de}
                onChange={e => alterar('riscar_de', e.target.checked)} />
              <label className="form-check-label" htmlFor="plaq-riscar-de" style={{ fontSize: '0.9rem' }}>
                Riscar o preço <b>DE</b> (traço vermelho)
              </label>
            </div>
          </div>

          {/* Prévia */}
          <div className="col-lg-6">
            <label className="form-label fw-semibold mb-1" style={{ fontSize: '0.85rem' }}>Prévia</label>
            <div style={{ width: '100%', maxWidth: 476, aspectRatio: '210 / 148', overflow: 'hidden', border: '1px solid #ddd', borderRadius: 6, background: '#fff', position: 'relative' }}>
              {previewHtml && (
                <iframe title="Prévia da plaquinha" srcDoc={previewHtml} sandbox=""
                  style={{ width: 794, height: 560, border: 0, transform: 'scale(0.6)', transformOrigin: '0 0', position: 'absolute', top: 0, left: 0 }} />
              )}
            </div>
            <small className="text-muted d-block mt-1" style={{ fontSize: '0.76rem' }}>Produto de exemplo. Salva sozinho — vale para todas as plaquinhas desta campanha (Por Campanha e Por Loja).</small>
          </div>
        </div>
      )}
    </div>
  );
}
